import React from 'react';
import { Bookmark, Lock } from 'lucide-react';
import { BasketIcon } from '../BasketIcon';
import { usePremium } from '../Subscription/PremiumProvider';
import { isLocked } from '../../lib/premiumLock';

/**
 * Compact basket chip for Connections default strip / All baskets grid.
 * Shows a pin badge only when pinned (tap to unpin). Pinning happens in the opened basket view.
 */
const ConnectionsBasketChip = ({ basket, onClick, onTogglePin, selected = false }) => {
  const { isPremium, isLoading } = usePremium();
  const topics = basket?.topics || [];
  const ripeCount = topics.filter((topic) => topic.is_ripe).length;
  const total = topics.length;
  const isEmpty = total === 0;
  const isPinned = Boolean(basket?.is_pinned);
  const showPremiumLock = isLocked(basket, isPremium, isLoading);

  const handleClick = () => {
    onClick?.(basket);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const handleUnpinClick = (event) => {
    event.stopPropagation();
    onTogglePin?.(basket);
  };

  const openButton = (
    <button
      type="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-pressed={selected}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-left focus:outline-none focus:ring-2 focus:ring-cranberry-700 ${
        selected
          ? 'border-neutral-900 bg-neutral-900 text-white hover:bg-neutral-800'
          : 'border-neutral-900 bg-white hover:bg-neutral-50'
      } ${isPinned ? 'w-full' : 'shrink-0 max-w-[240px]'} ${
        !selected && isEmpty ? 'text-neutral-500' : ''
      }`}
      aria-label={`Open basket ${basket?.name || ''}${isEmpty ? ' (empty)' : ''}`}
    >
      <span
        className={`shrink-0 scale-75 [&_svg]:h-5 [&_svg]:w-5 [&_svg]:mr-0 ${
          isEmpty && !selected ? 'opacity-60' : ''
        }`}
        aria-hidden="true"
      >
        <BasketIcon />
      </span>
      <span
        className={`text-xs font-semibold tabular-nums px-1.5 py-0.5 rounded-md shrink-0 ${
          selected
            ? 'text-white bg-neutral-700'
            : isEmpty
              ? 'text-neutral-500 bg-neutral-100'
              : 'text-neutral-600 bg-neutral-100'
        }`}
      >
        {isEmpty ? 'Empty' : `${ripeCount}/${total}`}
      </span>
      <span
        className={`text-sm font-semibold truncate ${
          selected
            ? 'text-white'
            : isEmpty
              ? 'text-neutral-500'
              : 'text-neutral-900'
        }`}
      >
        {basket?.name}
      </span>
      {showPremiumLock && (
        <span
          className={`inline-flex items-center gap-0.5 shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded-full px-1.5 py-0.5 border ${
            selected
              ? 'text-white bg-neutral-700 border-neutral-500'
              : 'text-neutral-500 bg-neutral-100 border-neutral-300'
          }`}
        >
          <Lock size={10} aria-hidden="true" />
          Premium
        </span>
      )}
    </button>
  );

  if (!isPinned) {
    return openButton;
  }

  return (
    <div className="relative inline-flex shrink-0 max-w-[240px]">
      {openButton}
      <button
        type="button"
        onClick={handleUnpinClick}
        className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full border-2 border-white bg-butter-500 text-butter-900 focus:outline-none focus:ring-2 focus:ring-cranberry-700"
        aria-label={`Unpin ${basket?.name}`}
        aria-pressed={true}
      >
        <Bookmark size={11} aria-hidden="true" />
      </button>
    </div>
  );
};

export default ConnectionsBasketChip;
